import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Send, Clock, CheckCircle, XCircle, FileText, User, Phone, Building2, Edit2, CreditCard, ExternalLink } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  new: { label: 'Neu', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'In Bearbeitung', color: 'bg-yellow-100 text-yellow-700' },
  offer_sent: { label: 'Offerte gesendet', color: 'bg-purple-100 text-purple-700' },
  accepted: { label: 'Angenommen', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' },
  closed: { label: 'Geschlossen', color: 'bg-gray-100 text-gray-700' },
};

const offerStatusConfig = {
  draft: { label: 'Entwurf', color: 'bg-gray-100 text-gray-700' },
  sent: { label: 'Gesendet', color: 'bg-blue-100 text-blue-700' },
  accepted: { label: 'Angenommen', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' },
  expired: { label: 'Abgelaufen', color: 'bg-orange-100 text-orange-700' },
  paid: { label: 'Bezahlt', color: 'bg-emerald-100 text-emerald-700' },
};

export default function CustomerPortalPage() {
  const { token, user } = useAuth();
  const [activeTab, setActiveTab] = useState('inquiries');
  const [inquiries, setInquiries] = useState([]);
  const [offers, setOffers] = useState([]);
  const [profile, setProfile] = useState(null);
  const [locations, setLocations] = useState([]);
  const [showNewInquiry, setShowNewInquiry] = useState(false);
  const [editingProfile, setEditingProfile] = useState(false);
  const [loading, setLoading] = useState(true);

  const [inquiryForm, setInquiryForm] = useState({
    location_id: '', subject: '', message: '', desired_start_date: '', desired_end_date: ''
  });
  const [profileForm, setProfileForm] = useState({ name: '', phone: '', company: '' });

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => { loadData(); }, [token]);

  const loadData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [inqRes, offRes, profRes, locRes] = await Promise.all([
        fetch(`${API}/api/my/inquiries`, { headers }),
        fetch(`${API}/api/my/offers`, { headers }),
        fetch(`${API}/api/my/profile`, { headers }),
        fetch(`${API}/api/locations`),
      ]);
      setInquiries(await inqRes.json());
      setOffers(await offRes.json());
      const p = await profRes.json();
      setProfile(p);
      setProfileForm({ name: p.name || '', phone: p.phone || '', company: p.company || '' });
      setLocations(await locRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const submitInquiry = async () => {
    if (!inquiryForm.subject || !inquiryForm.message) {
      toast.error('Bitte füllen Sie Betreff und Nachricht aus.');
      return;
    }
    try {
      const res = await fetch(`${API}/api/inquiries`, {
        method: 'POST', headers, body: JSON.stringify(inquiryForm),
      });
      if (res.ok) {
        toast.success('Anfrage erfolgreich gesendet!');
        setShowNewInquiry(false);
        setInquiryForm({ location_id: '', subject: '', message: '', desired_start_date: '', desired_end_date: '' });
        loadData();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Fehler beim Senden');
      }
    } catch (e) { toast.error('Netzwerkfehler'); }
  };

  const updateProfile = async () => {
    try {
      const res = await fetch(`${API}/api/my/profile`, {
        method: 'PUT', headers, body: JSON.stringify(profileForm),
      });
      if (res.ok) {
        const updated = await res.json();
        setProfile(updated);
        setEditingProfile(false);
        toast.success('Profil aktualisiert!');
      }
    } catch (e) { toast.error('Fehler beim Aktualisieren'); }
  };

  const tabs = [
    { id: 'inquiries', label: 'Anfragen', count: inquiries.length },
    { id: 'offers', label: 'Offerten', count: offers.length },
    { id: 'profile', label: 'Mein Profil', count: null },
  ];

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><p className="text-[hsl(var(--muted-foreground))]">Laden...</p></div>;

  return (
    <div className="max-w-5xl mx-auto py-8 px-4" data-testid="customer-portal">
      <h1 className="text-2xl font-bold mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Kundenbereich</h1>

      {/* Tab Navigation */}
      <div className="flex gap-1 mb-6 bg-[hsl(var(--surface-2))] p-1 rounded-lg w-fit" data-testid="portal-tabs">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`tab-${t.id}`}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${activeTab === t.id ? 'bg-white shadow text-[hsl(var(--foreground))]' : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'}`}>
            {t.label} {t.count !== null && <span className="ml-1 text-xs opacity-60">({t.count})</span>}
          </button>
        ))}
      </div>

      {/* INQUIRIES TAB */}
      {activeTab === 'inquiries' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Meine Anfragen</h2>
            <Dialog open={showNewInquiry} onOpenChange={setShowNewInquiry}>
              <DialogTrigger asChild>
                <Button data-testid="new-inquiry-btn"><Send className="w-4 h-4 mr-2" />Neue Anfrage</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Neue Anfrage erstellen</DialogTitle></DialogHeader>
                <div className="space-y-4 mt-2">
                  <div>
                    <Label>Standort (optional)</Label>
                    <Select value={inquiryForm.location_id} onValueChange={v => setInquiryForm(p => ({ ...p, location_id: v }))}>
                      <SelectTrigger data-testid="inquiry-location-select"><SelectValue placeholder="Standort wählen..." /></SelectTrigger>
                      <SelectContent>
                        {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Betreff *</Label>
                    <Input data-testid="inquiry-subject" value={inquiryForm.subject} onChange={e => setInquiryForm(p => ({ ...p, subject: e.target.value }))} placeholder="z.B. Werbefläche für Neueröffnung" />
                  </div>
                  <div>
                    <Label>Nachricht *</Label>
                    <Textarea data-testid="inquiry-message" value={inquiryForm.message} onChange={e => setInquiryForm(p => ({ ...p, message: e.target.value }))} placeholder="Beschreiben Sie Ihren Bedarf..." rows={4} />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Wunsch-Start</Label><Input data-testid="inquiry-start" type="date" value={inquiryForm.desired_start_date} onChange={e => setInquiryForm(p => ({ ...p, desired_start_date: e.target.value }))} /></div>
                    <div><Label>Wunsch-Ende</Label><Input data-testid="inquiry-end" type="date" value={inquiryForm.desired_end_date} onChange={e => setInquiryForm(p => ({ ...p, desired_end_date: e.target.value }))} /></div>
                  </div>
                  <Button data-testid="submit-inquiry-btn" onClick={submitInquiry} className="w-full"><Send className="w-4 h-4 mr-2" />Anfrage senden</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
          {inquiries.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-[hsl(var(--muted-foreground))]">Noch keine Anfragen. Erstellen Sie Ihre erste Anfrage!</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {inquiries.map(inq => {
                const s = statusConfig[inq.status] || statusConfig.new;
                return (
                  <Card key={inq.id} data-testid={`inquiry-${inq.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{inq.subject}</h3>
                          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">{inq.message?.substring(0, 120)}{inq.message?.length > 120 ? '...' : ''}</p>
                          {inq.location && <p className="text-sm mt-1"><span className="font-medium">Standort:</span> {inq.location.name}</p>}
                          {inq.desired_start_date && <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1">Wunschzeitraum: {inq.desired_start_date} – {inq.desired_end_date}</p>}
                          {inq.admin_notes && <p className="text-sm mt-2 p-2 bg-[hsl(var(--surface-2))] rounded"><strong>Admin:</strong> {inq.admin_notes}</p>}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                          <span className="text-xs text-[hsl(var(--muted-foreground))]">{new Date(inq.created_at).toLocaleDateString('de-CH')}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* OFFERS TAB */}
      {activeTab === 'offers' && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Meine Offerten</h2>
          {offers.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-[hsl(var(--muted-foreground))]">Noch keine Offerten erhalten.</p></CardContent></Card>
          ) : (
            <div className="space-y-3">
              {offers.map(offer => {
                const s = offerStatusConfig[offer.status] || offerStatusConfig.draft;
                const total = (offer.price || 0) + (offer.services_price || 0);
                return (
                  <Card key={offer.id} data-testid={`offer-${offer.id}`}>
                    <CardContent className="py-4">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold">{offer.location?.name || 'Standort'}</h3>
                          <p className="text-sm text-[hsl(var(--muted-foreground))]">{offer.location?.address}</p>
                          <div className="flex gap-4 mt-2 text-sm">
                            <span>Zeitraum: {new Date(offer.start_date).toLocaleDateString('de-CH')} – {new Date(offer.end_date).toLocaleDateString('de-CH')}</span>
                          </div>
                          {offer.notes && <p className="text-sm mt-1 italic text-[hsl(var(--muted-foreground))]">"{offer.notes}"</p>}
                          {offer.can_pay && offer.token && (
                            <a
                              href={`/offer/${offer.id}?token=${offer.token}`}
                              className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 bg-[hsl(var(--primary))] text-white text-sm font-medium rounded-md hover:opacity-90 transition-opacity"
                              data-testid={`pay-offer-link-${offer.id}`}
                            >
                              <CreditCard className="w-4 h-4" />
                              Offerte ansehen & bezahlen
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
                          <span className="text-xl font-bold text-[hsl(var(--primary))]">CHF {total.toFixed(2)}</span>
                          {offer.valid_until && <span className="text-xs text-[hsl(var(--muted-foreground))]">Gültig bis: {new Date(offer.valid_until).toLocaleDateString('de-CH')}</span>}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* PROFILE TAB */}
      {activeTab === 'profile' && profile && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Mein Profil</CardTitle>
                <CardDescription>Ihre persönlichen Daten und Kontaktinformationen</CardDescription>
              </div>
              <Button variant="outline" onClick={() => setEditingProfile(!editingProfile)} data-testid="edit-profile-btn">
                <Edit2 className="w-4 h-4 mr-2" />{editingProfile ? 'Abbrechen' : 'Bearbeiten'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {editingProfile ? (
              <div className="space-y-4 max-w-md">
                <div><Label>Name</Label><Input data-testid="profile-name" value={profileForm.name} onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} /></div>
                <div><Label>Telefon</Label><Input data-testid="profile-phone" value={profileForm.phone} onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} placeholder="+41 79 ..." /></div>
                <div><Label>Firma</Label><Input data-testid="profile-company" value={profileForm.company} onChange={e => setProfileForm(p => ({ ...p, company: e.target.value }))} placeholder="Ihre Firma" /></div>
                <Button data-testid="save-profile-btn" onClick={updateProfile}>Speichern</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center gap-3"><User className="w-5 h-5 text-[hsl(var(--muted-foreground))]" /><div><p className="text-sm text-[hsl(var(--muted-foreground))]">Name</p><p className="font-medium">{profile.name || '—'}</p></div></div>
                <div className="flex items-center gap-3"><span className="w-5 h-5 text-[hsl(var(--muted-foreground))] text-center">@</span><div><p className="text-sm text-[hsl(var(--muted-foreground))]">E-Mail</p><p className="font-medium">{profile.email}</p></div></div>
                <div className="flex items-center gap-3"><Phone className="w-5 h-5 text-[hsl(var(--muted-foreground))]" /><div><p className="text-sm text-[hsl(var(--muted-foreground))]">Telefon</p><p className="font-medium">{profile.phone || '—'}</p></div></div>
                <div className="flex items-center gap-3"><Building2 className="w-5 h-5 text-[hsl(var(--muted-foreground))]" /><div><p className="text-sm text-[hsl(var(--muted-foreground))]">Firma</p><p className="font-medium">{profile.company || '—'}</p></div></div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
